/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import {
  type TopNavProductDataResultsModel,
  type TopNavProductDataResultsResponse,
} from '../interfaceV2';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { useLocale } from '@/hooks/useLocale';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useSegments } from '@/features/header/api/getSegments';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { usePageType } from '@/features/category/api/getPageType';
import { useHeaderData } from '@/features/header/api/getHeader';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { constructPartGroupImageUrl } from '@/utils/common';
import { type Locale } from '@/types/i18n';
import {
  getExperimentId,
  getSearchExperimentConfig,
  selectSearchExperimentUrl,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const PRODUCT_DATA_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-navigation-search/v1/browse-categories`;
const ES_PRODUCT_DATA_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/browse-categories`;

export type NavigationMenusOptions = {
  country: string;
  locale: Locale;
  preview: boolean;
  categoryIdPath?: string | string[];
  storeId?: number;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  makeModelYearPath?: string;
  vehicleQuestions?: string | undefined;
  userSegments?: string;
  acesEnabled?: boolean;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

const getProductDataResults = async (
  options: NavigationMenusOptions,
  axiosInstance?: AxiosInstance,
) => {
  const {
    country,
    locale,
    preview,
    categoryIdPath,
    storeId,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    makeModelYearPath,
    vehicleQuestions,
    userSegments,
    acesEnabled,
    experimentId,
  } = options;

  const customerType = 'B2C';
  const salesChannel = 'ECOMM';

  const normalizedExperimentId = getExperimentId(experimentId, acesEnabled ?? false);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: acesEnabled ?? false,
  });

  const finalURL = selectSearchExperimentUrl(
    searchExpConfig,
    PRODUCT_DATA_URL,
    ES_PRODUCT_DATA_URL,
  );

  const baseParams = {
    customerType,
    salesChannel,
    country,
    preview,
    categoryIdPath,
    storeId,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    makeModelYearPath,
    vehicleQuestions,
    userSegments,
  };

  // Hash userId if provided (for both client and server-side calls)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, baseParams, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });
  const headers = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).get<TopNavProductDataResultsResponse>(finalURL, {
    params: enhancedParams,
    ...(headers && { headers }),
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    paramsSerializer: { indexes: null },
  });
  return topNavDataSelector(response.data, locale);
};

const {
  useData,
  prefetch: prefetchProductDataTopNavMenuV2,
  getFromCache: getProductDataTopNavMenuFromCacheV2,
} = createQuery<TopNavProductDataResultsModel, NavigationMenusOptions>(
  'topNavProductData',
  getProductDataResults,
);

export const topNavDataSelector = (
  data: TopNavProductDataResultsResponse,
  locale: Locale,
): TopNavProductDataResultsModel => {
  const { breadCrumbs, makeName, makeId, modelName, modelId } = data;

  const rootCategories: TopNavProductDataResultsModel['rootCategories'] =
    data.categoryResults?.map((item) => ({
      topNavServletPath: item.taxonomyPath ?? '',
      taxonomyPath: item.taxonomyPath,
      displayName: item.categoryName ?? '',
      text: item.categoryName ?? '',
      catName: item.categoryName ?? '',
      seoUrl: item.canonicalUrl ?? '',
      parentUrlState: item.canonicalUrl ?? '',
      categoryIdPath: item.categoryIdPath ?? '',
      pageType: item.pageType ?? '',
      shopByCategory:
        item.childCategories?.map((childCategory) => ({
          name: childCategory.categoryName ?? '',
          seoUrl: childCategory.canonicalUrl ?? '',
          pageType: childCategory.pageType ?? '',
          taxonomyPath: childCategory.taxonomyPath ?? '',
          id: childCategory.categoryIdPath ?? '',
        })) ?? [],
      childCategories:
        item.childCategories?.map((childCategory) => ({
          label: childCategory.categoryName ?? '',
          seoUrl: childCategory.canonicalUrl ?? '',
          canonicalPath: childCategory.canonicalUrl ?? '',
          refPageType: childCategory.pageType,
          partGroupId: childCategory.partGroupId,
          taxonomyPath: childCategory.taxonomyPath,
          categoryIdPath: childCategory.categoryIdPath,
          pageType: childCategory.pageType,
        })) ?? [],
    })) ?? [];

  const popularCategories: TopNavProductDataResultsModel['popularCategories'] =
    data.popularParts?.map((popularCategory) => ({
      label: popularCategory.displayName ?? '',
      seoUrl: popularCategory.canonicalUrl ?? '',
      canonicalPath: popularCategory.canonicalUrl,
      refPageType: popularCategory.pageType ?? '',
      partGroupId: popularCategory.partGroupId,
      categoryIdPath: popularCategory.categoryIdPath ?? '',
      taxonomyPath: popularCategory.taxonomyPath ?? '',
      properties: {
        seoUrl: popularCategory.canonicalUrl ?? '',
        refPageType: popularCategory.pageType ?? '',
        imageUrl: constructPartGroupImageUrl(popularCategory.partGroupId, locale) ?? '',
      },
    })) ?? [];

  return {
    breadCrumbs,
    rootCategories,
    popularCategories,
    make: makeName,
    makeId,
    model: modelName,
    modelId,
  };
};

type UseTopNavSubCategoriesOptions = {
  isNavigatingBack?: boolean;
  level?: number;
  categoryIdPath?: string | string[];
  isEnabled?: boolean;
  isTaxonomySelector?: boolean;
  isHamburgerMenu?: boolean;
  csSdkLocale?: Locale;
};

export const useProductDataTopNavMenuV2 = <T = TopNavProductDataResultsModel>({
  isNavigatingBack,
  level = 0,
  categoryIdPath = '',
  isEnabled = true,
  isTaxonomySelector = false,
  isHamburgerMenu = false,
  csSdkLocale = 'en-US',
  select,
}: UseTopNavSubCategoriesOptions & {
  select?: (data: TopNavProductDataResultsModel) => T;
} = {}) => {
  const { data: headerData } = useHeaderData();
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
    true,
  ) as string;
  const locale = useLocale();
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const preferredVehicle = usePreferredVehicle();
  const { data: storeDetailsData } = useStoreDetailsData();
  const { data: segments } = useSegments();
  const { data: pageTypeData } = usePageType();
  const vertexParams = useVertexPersonalizationParams();

  const enableQueryCall = (level > 0 || isNavigatingBack || level === -1) && isEnabled;

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams =
    isTaxonomySelector || isHamburgerMenu
      ? {}
      : validateAndFilterVehicleParams({
          year: preferredVehicle?.year,
          makeId: preferredVehicle?.makeId,
          modelId: preferredVehicle?.modelId,
          vehicleTypeId: preferredVehicle?.vehicleTypeId,
          vehicleQuestions: vehicleQuestions || undefined,
        });

  return useData({
    country: getCountryFromLocale(!isTaxonomySelector ? locale : csSdkLocale),
    locale: !isTaxonomySelector ? locale : csSdkLocale,
    enabled:
      (acesEnabled || isTaxonomySelector) && enableQueryCall && vertexParams.isMyProfileReady,
    preview: showXMPreviewDate(),
    categoryIdPath,
    ...(isTaxonomySelector || isHamburgerMenu
      ? {}
      : {
          storeId: Number(storeDetailsData?.storeNumber) || undefined,
          ...validatedVehicleParams,
          makeModelYearPath:
            pageTypeData?.pageType === 'CategoryGrouping'
              ? pageTypeData?.makeModelYearPath || undefined
              : undefined,
        }),
    ...(!acesEnabled && { userSegments: segments?.sort().join(',') || undefined }),
    acesEnabled,
    ...(acesEnabled && experimentId && { experimentId }),
    staleTime: 30 * 60000,
    select,
  });
};

export { prefetchProductDataTopNavMenuV2, getProductDataTopNavMenuFromCacheV2 };
